import { useState, useEffect } from "react";
import { apiClient } from "../App";
import Layout from "../components/Layout";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { DollarSign, Calendar } from "lucide-react";

const MyPayments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await apiClient.get('/me/payments');
        setPayments(res.data.payments);
      } catch (err) {
        console.error("Failed to fetch payments:", err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const dateOnly = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    return new Date(dateOnly + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const totalPaid = payments.reduce((sum, p) => sum + (p.amount_paid || 0), 0);

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6" data-testid="my-payments-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#0A192F] font-['Manrope'] tracking-tight">My Payments</h1>
            <p className="text-sm text-slate-500 mt-1">Your payment history</p>
          </div>
          <Badge variant="outline" className="text-slate-500">{payments.length} payments | Total: {formatCurrency(totalPaid)}</Badge>
        </div>

        <Card className="border-slate-200">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Payment Date</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Period</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Method</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Amount</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Adjustments</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={5}><div className="h-12 bg-slate-100 rounded animate-pulse"></div></TableCell>
                    </TableRow>
                  ))
                ) : payments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12">
                      <DollarSign className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                      <p className="text-slate-500">No payments yet</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  payments.map((p) => (
                    <TableRow key={p.payment_id} data-testid="my-payment-row">
                      <TableCell className="text-slate-600">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          {formatDate(p.payment_date)}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">
                        {formatDate(p.pay_period_start)} - {formatDate(p.pay_period_end)}
                      </TableCell>
                      <TableCell className="capitalize">{p.payment_method}</TableCell>
                      <TableCell className="font-semibold text-[#10B981]">{formatCurrency(p.amount_paid)}</TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-0.5">
                          {p.extra_amount > 0 && (
                            <span className="text-sm text-[#10B981]">+{formatCurrency(p.extra_amount)}{p.extra_reason && <span className="text-slate-400 ml-1">({p.extra_reason})</span>}</span>
                          )}
                          {p.deduction_amount > 0 && (
                            <span className="text-sm text-[#E11D48]">-{formatCurrency(p.deduction_amount)}{p.deduction_reason && <span className="text-slate-400 ml-1">({p.deduction_reason})</span>}</span>
                          )}
                          {!p.extra_amount && !p.deduction_amount && "-"}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default MyPayments;
